// Pure, framework-free validation + request assembly for the KYB (legal-entity)
// onboarding form (the NOT_CONNECTED surface of the split flow). No React, no
// translate() — validation returns stable error *codes* so it stays
// unit-testable and the consuming component owns localization. Mirrors
// `paymentsSetup.validation.ts`.
//
// REDACTION: every PII field here (SSN, DOB, beneficial-owner identities,
// document bytes) lives ONLY in the form's local state and this builder's
// return value. It transits to the provider on the single POST and is never
// persisted, logged, or placed in the React Query cache — the parent form
// unmounts on a successful submit (the KYB_PENDING waiting card replaces it),
// so the values never survive past submit.

import { parsePhoneNumberFromString } from 'libphonenumber-js'
import moment from 'moment-timezone'
import {
  KybAddress,
  KybAssociation,
  KybBusinessInput,
  KybDocument,
  KybDocumentType,
  KybIndividual,
  KybRelationshipType,
  LegalStructure,
} from 'src/queries/payments/payments.types'

// ─── Form value shapes (flat strings for text inputs) ────────────

export interface KybAddressValues {
  line1: string
  line2: string
  locality: string
  region: string
  postalCode: string
  country: string
}

export interface BeneficialOwnerValues {
  /** Index 0 of `beneficialOwners` is always the control person and carries
   * `control_person` implicitly; this marks whether they ALSO hold 25%+. Every
   * later entry is a beneficial owner by construction, so this is true there.
   * Wire `relationshipTypes` is derived from position + this flag. */
  isBeneficialOwner: boolean
  firstName: string
  lastName: string
  dateOfBirth: string
  /** Required per owner (the rebased API mandates `individual.email`). */
  email: string
  /** String in the form; required + numeric when `beneficial_owner`. */
  ownershipPercentage: string
  title: string
  ssn: string
  phoneNumber: string
  address: KybAddressValues
}

export interface KybDocumentValues {
  documentType: KybDocumentType
  filename: string
  fileDataBase64: string
}

export interface KybFormValues {
  legalBusinessName: string
  legalStructure: LegalStructure | ''
  dateFormed: string
  businessDescription: string
  intendedUse: string
  /** String in the form; parsed to an integer monthly-USD volume. */
  expectedActivityVolume: string
  usEin: string
  email: string
  website: string
  /** Primary business phone (E.164). */
  phoneNumber: string
  businessAddress: KybAddressValues
  includeRegisteredAddress: boolean
  registeredAddress: KybAddressValues
  /** Whether anyone holds 25%+. "none" is legitimate (a public company or a
   * regulated institution) and MT accepts a legal entity with a control person
   * and no beneficial owners, so it suppresses the list entirely. */
  ownershipChoice: KybOwnershipChoice
  beneficialOwners: BeneficialOwnerValues[]
  /** The mandatory EIN letter for business legal entities. Kept separate from
   * `documents` so it always has its own dedicated dropzone + requirement. */
  einLetterDocument: KybDocumentValues | null
  /** Additional (non-EIN) supporting documents — always optional. */
  documents: KybDocumentValues[]
}

// ─── Error codes (translate-free; the component maps these to strings) ──

export type KybOwnershipChoice = 'hasOwners' | 'noOwners'

export type KybFieldError =
  | 'LEGAL_NAME_REQUIRED'
  | 'LEGAL_STRUCTURE_REQUIRED'
  | 'DATE_FORMED_REQUIRED'
  | 'DATE_FORMED_INVALID'
  | 'DATE_FORMED_FUTURE'
  | 'BUSINESS_DESCRIPTION_REQUIRED'
  | 'INTENDED_USE_REQUIRED'
  | 'ACTIVITY_VOLUME_REQUIRED'
  | 'ACTIVITY_VOLUME_INVALID'
  | 'EIN_REQUIRED'
  | 'EIN_INVALID'
  | 'EMAIL_REQUIRED'
  | 'EMAIL_INVALID'
  | 'WEBSITE_INVALID'
  | 'PHONE_REQUIRED'
  | 'PHONE_INVALID'
  | 'ADDRESS_LINE1_REQUIRED'
  | 'ADDRESS_LOCALITY_REQUIRED'
  | 'ADDRESS_REGION_REQUIRED'
  | 'ADDRESS_POSTAL_CODE_REQUIRED'
  | 'ADDRESS_COUNTRY_REQUIRED'
  | 'OWNER_FIRST_NAME_REQUIRED'
  | 'OWNER_LAST_NAME_REQUIRED'
  | 'OWNER_DOB_REQUIRED'
  | 'OWNER_DOB_INVALID'
  | 'OWNER_DOB_UNDERAGE'
  | 'OWNER_EMAIL_REQUIRED'
  | 'OWNER_EMAIL_INVALID'
  | 'OWNER_OWNERSHIP_REQUIRED'
  | 'OWNER_OWNERSHIP_INVALID'
  | 'OWNERSHIP_TOTAL_INVALID'
  | 'OWNER_SSN_REQUIRED'
  | 'OWNER_SSN_INVALID'
  | 'OWNER_PHONE_REQUIRED'
  | 'OWNER_PHONE_INVALID'
  | 'CONTROL_PERSON_REQUIRED'
  | 'EIN_LETTER_REQUIRED'

export interface KybAddressErrors {
  line1?: KybFieldError
  locality?: KybFieldError
  region?: KybFieldError
  postalCode?: KybFieldError
  country?: KybFieldError
}

export interface BeneficialOwnerErrors {
  firstName?: KybFieldError
  lastName?: KybFieldError
  dateOfBirth?: KybFieldError
  email?: KybFieldError
  ownershipPercentage?: KybFieldError
  ssn?: KybFieldError
  phoneNumber?: KybFieldError
  address?: KybAddressErrors
}

export interface KybFormErrors {
  legalBusinessName?: KybFieldError
  legalStructure?: KybFieldError
  dateFormed?: KybFieldError
  businessDescription?: KybFieldError
  intendedUse?: KybFieldError
  expectedActivityVolume?: KybFieldError
  usEin?: KybFieldError
  email?: KybFieldError
  website?: KybFieldError
  phoneNumber?: KybFieldError
  businessAddress?: KybAddressErrors
  registeredAddress?: KybAddressErrors
  /** Index-aligned with `values.beneficialOwners`. */
  beneficialOwners?: BeneficialOwnerErrors[]
  /** The form component always seeds slot 0, but this module is pure and
   * separately consumable — an empty owner array must not validate. */
  controlPerson?: KybFieldError
  /** Aggregate ownership across all owners exceeded 100%. Not tied to a single
   * field — surfaced as a step-level note. */
  ownershipTotal?: KybFieldError
  /** Business legal entities must attach an EIN letter. */
  einLetterDocument?: KybFieldError
  /** Additional (non-EIN) documents are optional; set only from a server 422. */
  documents?: KybFieldError
}

// ─── Empty defaults ──────────────────────────────────────────────

/**
 * Default country for the KYB address forms in the MVP. The country picker is
 * pre-selected to the US (the only region set the backend reference data ships
 * today); mirrors `StateSelect`'s `DEFAULT_STATE_SELECT_COUNTRY`.
 */
export const DEFAULT_KYB_COUNTRY = 'US'

export const EMPTY_KYB_ADDRESS: KybAddressValues = {
  line1: '',
  line2: '',
  locality: '',
  region: '',
  postalCode: '',
  country: DEFAULT_KYB_COUNTRY,
}

export const EMPTY_BENEFICIAL_OWNER: BeneficialOwnerValues = {
  isBeneficialOwner: true,
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  ownershipPercentage: '',
  title: '',
  ssn: '',
  phoneNumber: '',
  address: EMPTY_KYB_ADDRESS,
}

export const EMPTY_KYB_FORM_VALUES: KybFormValues = {
  legalBusinessName: '',
  legalStructure: '',
  dateFormed: '',
  businessDescription: '',
  intendedUse: '',
  expectedActivityVolume: '',
  usEin: '',
  email: '',
  website: '',
  phoneNumber: '',
  businessAddress: EMPTY_KYB_ADDRESS,
  includeRegisteredAddress: false,
  ownershipChoice: 'hasOwners',
  registeredAddress: EMPTY_KYB_ADDRESS,
  beneficialOwners: [],
  einLetterDocument: null,
  documents: [],
}

// ─── Business-structure / document rules ─────────────────────────

/**
 * Legal structures MT treats as a *business* legal entity — for these it
 * mandates an EIN letter. `sole_proprietorship` is the individual-operated
 * exception (an individual legal entity), so the EIN letter stays optional for
 * it. Written as a set so the business/individual distinction is explicit and
 * extensible.
 */
export const BUSINESS_LEGAL_STRUCTURES: ReadonlySet<LegalStructure> =
  new Set<LegalStructure>([
    'corporation',
    'llc',
    'non_profit',
    'partnership',
    'trust',
  ])

export const isBusinessLegalStructure = (
  structure: LegalStructure | '',
): boolean => !!structure && BUSINESS_LEGAL_STRUCTURES.has(structure)

// ─── Field validators ────────────────────────────────────────────

/** Strip everything that is not a digit (EIN / SSN entry). */
export const digitsOnly = (value: string): string => value.replace(/\D/g, '')

/** US EIN: exactly 9 digits, no hyphen. */
export const isValidEin = (value: string): boolean =>
  /^\d{9}$/.test(digitsOnly(value))

/** US SSN: exactly 9 digits. */
export const isValidSsn = (value: string): boolean =>
  /^\d{9}$/.test(digitsOnly(value))

/** Valid phone: accepts formatted national input (e.g. "(212) 555-0123", the
 * shape the AsYouType-formatted field produces) as well as full E.164, parsed
 * with a US default. Normalized to E.164 for the API via `toE164Phone`. */
export const isValidE164Phone = (value: string): boolean => {
  const parsed = parsePhoneNumberFromString(value.trim(), 'US')
  return !!parsed && parsed.isValid()
}

/** Normalize any accepted phone input to E.164 for the API request. Falls back
 * to the trimmed raw value if it can't be parsed (validation blocks that case). */
export const toE164Phone = (value: string): string =>
  parsePhoneNumberFromString(value.trim(), 'US')?.number ?? value.trim()

/** `YYYY-MM-DD`, and a real calendar date. */
export const isValidIsoDate = (value: string): boolean => {
  const trimmed = value.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false
  const [year, month, day] = trimmed.split('-').map(Number)
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

/** Minimum age (years) an individual on the legal entity must be. */
export const ADULT_MIN_AGE = 18

/**
 * Date-only comparisons (moment, so they don't drift with the time-of-day or
 * the runner's timezone the way `new Date()` millisecond arithmetic does). Pass
 * only a value that already satisfies `isValidIsoDate`.
 */
/** True when `isoDate` (YYYY-MM-DD) falls after today's calendar day. */
export const isFutureIsoDate = (isoDate: string): boolean =>
  moment(isoDate.trim(), 'YYYY-MM-DD', true).isAfter(moment(), 'day')

/** Full calendar-years old as of today (whole years, leap-safe via moment). */
export const ageInYears = (isoDate: string): number =>
  moment().diff(moment(isoDate.trim(), 'YYYY-MM-DD', true), 'years')

// Upper bound on the parsed monthly volume. Guards against values that lose
// integer precision or overflow to Infinity (which JSON-serializes to null).
// TODO(EX-40413): confirm the real backend maximum and align this cap.
export const MAX_ACTIVITY_VOLUME = 1_000_000_000_000

/** Positive integer USD amount (allows thousands separators / whitespace),
 * within safe-integer range and the sane upper bound. */
export const isValidActivityVolume = (value: string): boolean => {
  const cleaned = value.replace(/[,\s]/g, '')
  if (!/^\d+$/.test(cleaned)) return false
  const parsed = Number(cleaned)
  return (
    Number.isSafeInteger(parsed) && parsed > 0 && parsed <= MAX_ACTIVITY_VOLUME
  )
}

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const isValidWebsite = (value: string): boolean =>
  /^(https?:\/\/)?[^\s.]+\.\S{2,}$/.test(value.trim())

/** Plain decimal only — `Number()` alone would also accept hex/exponent forms
 * ("0x64"), letting a value the user never meant ship on the wire. */
export const parseOwnershipPercentage = (value: string): number => {
  const trimmed = value.trim()
  return /^\d{1,3}(\.\d+)?$/.test(trimmed) ? Number(trimmed) : NaN
}

/** Beneficial-owner percentage accepted by the API: 25 through 100. */
const isValidOwnershipPercentage = (value: string): boolean => {
  const n = parseOwnershipPercentage(value)
  return n >= 25 && n <= 100
}

// ─── Address validation ──────────────────────────────────────────

/** Validate a KYB address (all subfields except `line2` required). */
export const validateKybAddress = (
  address: KybAddressValues,
): KybAddressErrors => {
  const errors: KybAddressErrors = {}
  if (!address.line1.trim()) errors.line1 = 'ADDRESS_LINE1_REQUIRED'
  if (!address.locality.trim()) errors.locality = 'ADDRESS_LOCALITY_REQUIRED'
  if (!address.region.trim()) errors.region = 'ADDRESS_REGION_REQUIRED'
  if (!address.postalCode.trim())
    errors.postalCode = 'ADDRESS_POSTAL_CODE_REQUIRED'
  if (!address.country.trim()) errors.country = 'ADDRESS_COUNTRY_REQUIRED'
  return errors
}

const isAddressErrorsEmpty = (errors: KybAddressErrors): boolean =>
  Object.keys(errors).length === 0

// ─── Beneficial-owner validation ─────────────────────────────────

/**
 * Whether the beneficial-owner list is part of the form at all. Hidden when
 * nobody holds 25%+, and when the control person already holds all of it —
 * no other 25%+ owner can exist alongside a 100% holder.
 */
export const beneficialOwnersShown = (values: KybFormValues): boolean => {
  if (values.ownershipChoice === 'noOwners') return false
  const control = values.beneficialOwners[0]
  const controlOwnsAll =
    control?.isBeneficialOwner &&
    parseOwnershipPercentage(control.ownershipPercentage) === 100
  return !controlOwnsAll
}

/** Validate one person. The control person occupies a different slot from the
 * beneficial owners after it, so its ownership rules differ. */
export const validateBeneficialOwner = (
  owner: BeneficialOwnerValues,
  isControlPerson = false,
): BeneficialOwnerErrors => {
  const errors: BeneficialOwnerErrors = {}

  if (!owner.firstName.trim()) errors.firstName = 'OWNER_FIRST_NAME_REQUIRED'
  if (!owner.lastName.trim()) errors.lastName = 'OWNER_LAST_NAME_REQUIRED'

  const dob = owner.dateOfBirth.trim()
  if (!dob) {
    errors.dateOfBirth = 'OWNER_DOB_REQUIRED'
  } else if (!isValidIsoDate(dob) || isFutureIsoDate(dob)) {
    errors.dateOfBirth = 'OWNER_DOB_INVALID'
  } else if (ageInYears(dob) < ADULT_MIN_AGE) {
    // Distinct from a malformed date: the value parsed fine, the person is
    // simply too young, and "enter a valid date" would send them hunting for a
    // typo that isn't there.
    errors.dateOfBirth = 'OWNER_DOB_UNDERAGE'
  }

  // Email is required per owner (the rebased API mandates `individual.email`).
  const email = owner.email.trim()
  if (!email) {
    errors.email = 'OWNER_EMAIL_REQUIRED'
  } else if (!isValidEmail(email)) {
    errors.email = 'OWNER_EMAIL_INVALID'
  }

  // A beneficial owner always carries a percentage. The control person only
  // does when they said they also hold 25%+.
  const isBeneficialOwner = isControlPerson ? owner.isBeneficialOwner : true
  if (isBeneficialOwner) {
    const pct = owner.ownershipPercentage.trim()
    if (!pct) {
      errors.ownershipPercentage = 'OWNER_OWNERSHIP_REQUIRED'
    } else if (!isValidOwnershipPercentage(pct)) {
      errors.ownershipPercentage = 'OWNER_OWNERSHIP_INVALID'
    }
  } else if (owner.ownershipPercentage.trim()) {
    // The API rejects a percentage on a control-person-only association.
    errors.ownershipPercentage = 'OWNER_OWNERSHIP_INVALID'
  }

  // SSN and an E.164 phone are required for every associated individual.
  const ssn = owner.ssn.trim()
  if (!ssn) {
    errors.ssn = 'OWNER_SSN_REQUIRED'
  } else if (!isValidSsn(ssn)) {
    errors.ssn = 'OWNER_SSN_INVALID'
  }
  const phoneNumber = owner.phoneNumber.trim()
  if (!phoneNumber) {
    errors.phoneNumber = 'OWNER_PHONE_REQUIRED'
  } else if (!isValidE164Phone(phoneNumber)) {
    errors.phoneNumber = 'OWNER_PHONE_INVALID'
  }

  const addressErrors = validateKybAddress(owner.address)
  if (!isAddressErrorsEmpty(addressErrors)) {
    errors.address = addressErrors
  }

  return errors
}

const isOwnerErrorsEmpty = (errors: BeneficialOwnerErrors): boolean =>
  Object.keys(errors).length === 0

// ─── Full-form validation ────────────────────────────────────────

/**
 * Validate the full KYB form. MT-mandatory fields are required; email / website
 * / registered address / additional beneficial owners / documents are optional
 * (but exactly one control person is required, and every provided value must be
 * well-formed).
 */
export const validateKybForm = (values: KybFormValues): KybFormErrors => {
  const errors: KybFormErrors = {}

  if (!values.legalBusinessName.trim())
    errors.legalBusinessName = 'LEGAL_NAME_REQUIRED'
  if (!values.legalStructure) errors.legalStructure = 'LEGAL_STRUCTURE_REQUIRED'

  const dateFormed = values.dateFormed.trim()
  if (!dateFormed) {
    errors.dateFormed = 'DATE_FORMED_REQUIRED'
  } else if (!isValidIsoDate(dateFormed)) {
    errors.dateFormed = 'DATE_FORMED_INVALID'
  } else if (isFutureIsoDate(dateFormed)) {
    errors.dateFormed = 'DATE_FORMED_FUTURE'
  }

  if (!values.businessDescription.trim())
    errors.businessDescription = 'BUSINESS_DESCRIPTION_REQUIRED'
  if (!values.intendedUse.trim()) errors.intendedUse = 'INTENDED_USE_REQUIRED'

  const volume = values.expectedActivityVolume.trim()
  if (!volume) {
    errors.expectedActivityVolume = 'ACTIVITY_VOLUME_REQUIRED'
  } else if (!isValidActivityVolume(volume)) {
    errors.expectedActivityVolume = 'ACTIVITY_VOLUME_INVALID'
  }

  const ein = values.usEin.trim()
  if (!ein) {
    errors.usEin = 'EIN_REQUIRED'
  } else if (!isValidEin(ein)) {
    errors.usEin = 'EIN_INVALID'
  }

  // Business email is required (the prototype's Business contact section marks
  // it required; MT accepts it as an optional org-level field, so a required UI
  // is the stricter, safe choice).
  const email = values.email.trim()
  if (!email) {
    errors.email = 'EMAIL_REQUIRED'
  } else if (!isValidEmail(email)) {
    errors.email = 'EMAIL_INVALID'
  }
  // Website is no longer collected in the form (the prototype dropped it), but
  // keep validating any value that survives in state for decode-safety.
  if (values.website.trim() && !isValidWebsite(values.website)) {
    errors.website = 'WEBSITE_INVALID'
  }

  const phone = values.phoneNumber.trim()
  if (!phone) {
    errors.phoneNumber = 'PHONE_REQUIRED'
  } else if (!isValidE164Phone(phone)) {
    errors.phoneNumber = 'PHONE_INVALID'
  }

  const businessAddressErrors = validateKybAddress(values.businessAddress)
  if (!isAddressErrorsEmpty(businessAddressErrors)) {
    errors.businessAddress = businessAddressErrors
  }

  if (values.includeRegisteredAddress) {
    const registeredErrors = validateKybAddress(values.registeredAddress)
    if (!isAddressErrorsEmpty(registeredErrors)) {
      errors.registeredAddress = registeredErrors
    }
  }

  // Index 0 is the control person by construction — there is no count to
  // check, but the slot itself must be occupied. Beyond it, only the owners
  // the form actually shows are validated.
  const controlPerson = values.beneficialOwners[0]
  if (!controlPerson) {
    errors.controlPerson = 'CONTROL_PERSON_REQUIRED'
  }
  const shownOwners = beneficialOwnersShown(values)
    ? values.beneficialOwners.slice(1)
    : []

  const ownerErrors = [
    ...(controlPerson ? [validateBeneficialOwner(controlPerson, true)] : []),
    ...shownOwners.map((owner) => validateBeneficialOwner(owner)),
  ]
  if (ownerErrors.some((e) => !isOwnerErrorsEmpty(e))) {
    errors.beneficialOwners = ownerErrors
  }

  // Aggregate ownership across the control person and every shown owner.
  const ownershipTotal = [
    ...(controlPerson?.isBeneficialOwner ? [controlPerson] : []),
    ...shownOwners,
  ].reduce((sum, owner) => {
    const pct = parseOwnershipPercentage(owner.ownershipPercentage)
    return pct > 0 ? sum + pct : sum
  }, 0)
  if (ownershipTotal > 100) {
    errors.ownershipTotal = 'OWNERSHIP_TOTAL_INVALID'
  }

  // MT rejects a business legal entity with no EIN letter attached. Additional
  // (non-EIN) `documents` stay optional regardless of legal structure.
  if (
    isBusinessLegalStructure(values.legalStructure) &&
    !values.einLetterDocument
  ) {
    errors.einLetterDocument = 'EIN_LETTER_REQUIRED'
  }

  return errors
}

/** True when the KYB form produced no errors anywhere in the tree. */
export const isKybFormValid = (errors: KybFormErrors): boolean => {
  const { businessAddress, registeredAddress, beneficialOwners, ...topLevel } =
    errors

  if (Object.values(topLevel).some((code) => code !== undefined)) return false
  if (businessAddress && !isAddressErrorsEmpty(businessAddress)) return false
  if (registeredAddress && !isAddressErrorsEmpty(registeredAddress))
    return false
  if (beneficialOwners && beneficialOwners.some((e) => !isOwnerErrorsEmpty(e)))
    return false
  return true
}

// ─── Request assembly ────────────────────────────────────────────

const buildAddress = (values: KybAddressValues): KybAddress => {
  const address: KybAddress = {
    line1: values.line1.trim(),
    locality: values.locality.trim(),
    region: values.region.trim(),
    postalCode: values.postalCode.trim(),
    country: values.country.trim(),
  }
  const line2 = values.line2.trim()
  if (line2) address.line2 = line2
  return address
}

const buildIndividual = (owner: BeneficialOwnerValues): KybIndividual => ({
  firstName: owner.firstName.trim(),
  lastName: owner.lastName.trim(),
  dateOfBirth: owner.dateOfBirth.trim(),
  // Email + SSN are required per owner now, so always send them.
  email: owner.email.trim(),
  phoneNumbers: [{ phoneNumber: toE164Phone(owner.phoneNumber) }],
  addresses: [buildAddress(owner.address)],
  ssn: digitsOnly(owner.ssn),
})

/** Wire roles come from the slot, not from a user-picked list: index 0 is the
 * control person and may additionally hold a stake; everyone after it is on the
 * list because they are a beneficial owner. */
const buildAssociation = (
  owner: BeneficialOwnerValues,
  isControlPerson: boolean,
): KybAssociation => {
  const relationshipTypes: KybRelationshipType[] = isControlPerson
    ? [
        'control_person',
        ...(owner.isBeneficialOwner ? (['beneficial_owner'] as const) : []),
      ]
    : ['beneficial_owner']
  const association: KybAssociation = {
    relationshipTypes,
    individual: buildIndividual(owner),
  }
  // MT rejects a percentage on a control-person-only association.
  const pct = parseOwnershipPercentage(owner.ownershipPercentage)
  if (!Number.isNaN(pct) && relationshipTypes.includes('beneficial_owner')) {
    association.ownershipPercentage = pct
  }
  const title = owner.title.trim()
  if (title) association.title = title
  return association
}

const buildDocument = (doc: KybDocumentValues): KybDocument => ({
  documentType: doc.documentType,
  filename: doc.filename,
  fileDataBase64: doc.fileDataBase64,
})

/**
 * Build the `POST /payments/connection` (KYB) request from validated form
 * state. Call only after `validateKybForm` returns no errors. Trims every
 * field and omits blank optionals.
 */
export const buildKybRequest = (values: KybFormValues): KybBusinessInput => {
  const request: KybBusinessInput = {
    legalBusinessName: values.legalBusinessName.trim(),
    // `legalStructure` is guaranteed non-empty post-validation.
    legalStructure: values.legalStructure as LegalStructure,
    dateFormed: values.dateFormed.trim(),
    businessDescription: values.businessDescription.trim(),
    intendedUse: values.intendedUse.trim(),
    expectedActivityVolume: Number(
      values.expectedActivityVolume.replace(/[,\s]/g, ''),
    ),
    usEin: digitsOnly(values.usEin),
    businessAddress: buildAddress(values.businessAddress),
    phoneNumbers: [{ phoneNumber: toE164Phone(values.phoneNumber) }],
  }

  const email = values.email.trim()
  if (email) request.email = email
  const website = values.website.trim()
  if (website) request.website = website

  if (values.includeRegisteredAddress) {
    request.registeredAddress = buildAddress(values.registeredAddress)
  }

  // Only the people the form actually collected: the control person always,
  // plus the beneficial owners when that list was shown. MT accepts a legal
  // entity with a control person and no beneficial owners.
  const associationOwners = [
    ...values.beneficialOwners.slice(0, 1),
    ...(beneficialOwnersShown(values) ? values.beneficialOwners.slice(1) : []),
  ]
  if (associationOwners.length > 0) {
    request.associations = associationOwners.map((owner, index) =>
      buildAssociation(owner, index === 0),
    )
  }

  // EIN letter first, then any additional (non-EIN) documents.
  const documents = [values.einLetterDocument, ...values.documents].filter(
    (doc): doc is KybDocumentValues => Boolean(doc),
  )
  if (documents.length > 0) {
    request.documents = documents.map(buildDocument)
  }

  return request
}
